import { IconButton, List, ListItemButton, ListItemIcon, ListItemText, Popover, Stack, Typography, Divider, Tooltip } from '@vapor/material'
import { Button } from '@mui/material'
import { FC, MouseEvent, useState, useCallback, useMemo } from 'react'
import { styled } from '@mui/material/styles'
import { AccountCircle } from '@mui/icons-material'
import { useAuth } from '../../hooks/auth/auth.hook'
import { LogoutButton } from '@ts-digital/digital-login-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCookieBite, faSparkles, faSquareQuestion } from '@fortawesome/pro-light-svg-icons'
import { cookieVars } from '../cookies-selector/open-dialog-var'
import { useTranslation } from 'react-i18next'

const UserButton = styled(Button)(({ theme }) => ({
  background: 'none !important',
  color: `${theme.palette.primary.contrastText} !important`,
  justifyContent: 'left !important',
  fontWeight: 'normal !important',
  ':hover': {
    background: 'hsl(199, 100%, 20%) !important',
    minHeight: '48px !important',
    height: '48px !important',
  },
}))

const UserMenu: FC = () => {
  const { auth, logout } = useAuth()
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null)
  const { t } = useTranslation()

  const decodedToken = useMemo(() => {
    if (!auth.loading && auth.isAuthenticated) {
      return JSON.parse(window.atob(auth.token.split('.')[1]))
    } else {
      return null
    }
  }, [auth])

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  if (!decodedToken) {
    return <></>
  } else {
    return (
      <>
        <UserButton sx={{ display: { xs: 'none', xl: 'inline-flex' } }} aria-describedby={id} startIcon={<AccountCircle />} onClick={handleClick}>
          {decodedToken.name}
        </UserButton>
        <IconButton sx={{ display: { xs: 'flex', xl: 'none' } }} aria-describedby={id} onClick={handleClick}>
          <Tooltip title={decodedToken.name}>
            <AccountCircle />
          </Tooltip>
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <Stack spacing={1} sx={{ p: 1 }}>
            <Stack>
              <Typography variant="titleXSmall">{decodedToken.name}</Typography>
              <Typography variant="caption">{decodedToken.email}</Typography>
            </Stack>
            <Divider />
            <List disablePadding sx={{ width: '220px' }}>
              <ListItemButton
                onClick={() => {
                  window.open('https://teamsystemhospitality.zendesk.com/hc/it/sections/15450654034205-Release-Notes', '_blank', 'noreferrer')
                }}
              >
                <ListItemIcon>
                  <FontAwesomeIcon icon={faSparkles} />
                </ListItemIcon>
                <ListItemText primary={t('pms.commons.last_news')} />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  window.open('https://teamsystemhospitality.zendesk.com/hc/it', '_blank', 'noreferrer')
                }}
              >
                <ListItemIcon>
                  <FontAwesomeIcon icon={faSquareQuestion} />
                </ListItemIcon>
                <ListItemText primary={t('pms.commons.guide')} />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  const oldValue = cookieVars()
                  cookieVars({ ...oldValue, openCookieDialog: true })
                  // window.manageCookies()
                  setAnchorEl(null)
                }}
              >
                <ListItemIcon>
                  <FontAwesomeIcon icon={faCookieBite} />
                </ListItemIcon>
                <ListItemText primary={t('pms.commons.manage_cookies')} />
              </ListItemButton>
            </List>
            {auth.isAuthenticated && auth.token && (
              <LogoutButton
                idToken={auth.idToken}
                buttonVariant="contained"
                redirectUrl={process.env.REACT_APP_URL ?? ''}
                env={process.env.REACT_APP_ENV as 'dev' | 'test' | 'prod'}
                beforeLogout={() => logout()}
                buttonText={t('commons.logout')}
              />
            )}
          </Stack>
        </Popover>
      </>
    )
  }
}

export default UserMenu
